import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Camera,
  ArrowLeft,
  X,
  Plus,
  Pencil,
  Download,
  Loader2,
  Image as ImageIcon,
  Sparkles,
  ChevronDown,
  Send,
  Coins,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const ASPECT_RATIOS = [
  { value: "9:16", label: "Portrait (9:16)" },
  { value: "16:9", label: "Landscape (16:9)" },
  { value: "1:1", label: "Square (1:1)" },
  { value: "4:5", label: "Feed (4:5)" },
];

// ============ LANDING VIEW ============
function LandingView({ onSelectMode }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center animate-fade-in" data-testid="photoshoot-landing">
      <h1
        className="text-4xl sm:text-5xl font-semibold mb-3 tracking-tight text-foreground"
        style={{ fontFamily: "'Clash Display', serif" }}
      >
        Photoshoot
      </h1>
      <p className="text-sm text-muted-foreground mb-2 text-center max-w-xl leading-relaxed">
        Choose a guided template for professional product shots or use our flexible editor to create anything you can imagine.
      </p>
      <button
        onClick={() => navigate("/photoshoot/gallery")}
        className="text-sm text-[#FF5A36] hover:underline mb-8"
        data-testid="view-gallery-link"
      >
        View My Photoshoots
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl">
        {/* Template Card */}
        <button
          onClick={() => onSelectMode("template")}
          className="group relative bg-card rounded-2xl p-6 text-left border border-border hover:border-[#FF5A36]/50 hover:shadow-lg transition-all duration-300 cursor-pointer"
          data-testid="photoshoot-template-card"
        >
          <h3 className="text-foreground font-medium text-lg mb-1">Create a product photoshoot</h3>
          <p className="text-muted-foreground text-sm mb-5">
            Choose a product image and templates to get professional shots
          </p>
          <div className="grid grid-cols-3 gap-2 rounded-xl overflow-hidden">
            {[
              "https://images.unsplash.com/photo-1621331462282-0c890b7c933b?w=200&h=200&fit=crop",
              "https://images.pexels.com/photos/14311564/pexels-photo-14311564.jpeg?auto=compress&w=200&h=200&fit=crop",
              "https://images.pexels.com/photos/7038225/pexels-photo-7038225.jpeg?auto=compress&w=200&h=200&fit=crop",
              "https://images.unsplash.com/photo-1668264514570-24b30c2e899f?w=200&h=200&fit=crop",
              "https://images.pexels.com/photos/29611520/pexels-photo-29611520.jpeg?auto=compress&w=200&h=200&fit=crop",
              "https://images.unsplash.com/photo-1757528649062-bc9bc892bac3?w=200&h=200&fit=crop",
            ].map((src, i) => (
              <div key={i} className="aspect-square rounded-lg overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              </div>
            ))}
          </div>
        </button>

        {/* Generate Card */}
        <button
          onClick={() => onSelectMode("generate")}
          className="group relative bg-card rounded-2xl p-6 text-left border border-border hover:border-[#FF5A36]/50 hover:shadow-lg transition-all duration-300 cursor-pointer"
          data-testid="photoshoot-generate-card"
        >
          <h3 className="text-foreground font-medium text-lg mb-1">Generate or edit an image</h3>
          <p className="text-muted-foreground text-sm mb-5">
            Describe the image you want with a prompt or edit an existing one
          </p>
          <div className="rounded-xl overflow-hidden aspect-[4/3]">
            <img
              src="https://images.unsplash.com/photo-1757528649062-bc9bc892bac3?w=600&h=450&fit=crop"
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        </button>
      </div>
    </div>
  );
}

// ============ TEMPLATE MODE ============
function TemplateMode({ onBack, onEdit }) {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [productImage, setProductImage] = useState(null);
  const [productImageUrl, setProductImageUrl] = useState("");
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [showAspectDropdown, setShowAspectDropdown] = useState(false);
  const [creditCost, setCreditCost] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/photoshoot/config`, { withCredentials: true });
      setCreditCost(res.data.credit_cost);
    } catch { /* use null as loading state */ }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/photoshoot/templates`, { withCredentials: true });
      setTemplates(res.data.templates || []);
      setCategories(["All", ...(res.data.categories || [])]);
    } catch {
      toast.error("Failed to load templates");
    }
  };

  const handleProductUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProductImage(URL.createObjectURL(file));

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/api/photoshoot/upload-product-image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setProductImageUrl(res.data.image_url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      setProductImage(null);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerate = async () => {
    if (!productImageUrl) return toast.error("Please upload a product image");
    if (!selectedTemplate) return toast.error("Please select a template");
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/photoshoot/generate/template`, {
        template_id: selectedTemplate.id,
        product_image_url: productImageUrl,
        aspect_ratio: aspectRatio,
      }, { withCredentials: true });
      setResult(res.data);
      toast.success("Photoshoot generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const filtered = selectedCategory === "All"
    ? templates
    : templates.filter((t) => t.category === selectedCategory);

  // RESULT VIEW
  if (result) {
    return (
      <ResultView
        imageUrl={result.generated_image_url}
        productImageUrl={productImage}
        templateImage={selectedTemplate?.image_url}
        templateName={selectedTemplate?.name}
        creditsUsed={result.credits_used}
        onBack={() => setResult(null)}
        onClose={onBack}
        onEdit={onEdit}
      />
    );
  }

  // LOADING VIEW
  if (generating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in" data-testid="photoshoot-loading">
        <div className="relative w-64 h-80 bg-card rounded-2xl border border-border flex flex-col items-center justify-center gap-4 shadow-sm">
          <Loader2 className="w-8 h-8 text-[#FF5A36] animate-spin" />
          <p className="text-muted-foreground text-sm">This may take a few minutes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="photoshoot-template-mode">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="template-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Camera className="w-5 h-5 text-muted-foreground" />
        <h2 className="text-xl font-semibold text-foreground">Product Photoshoot with Templates</h2>
      </div>
      <p className="text-muted-foreground text-sm mb-6 ml-8">
        Select your product and shot templates to generate new professional photos.
      </p>

      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Left: Product Image */}
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center justify-between mb-4">
            <span className="text-foreground font-medium text-sm">Product Image</span>
            {productImage && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-8 h-8 rounded-full bg-[#FF5A36] flex items-center justify-center hover:bg-[#e54e2e] transition-colors"
              >
                <Pencil className="w-3.5 h-3.5 text-white" />
              </button>
            )}
          </div>

          {productImage ? (
            <div className="relative">
              <img
                src={productImage}
                alt="Product"
                className="w-full rounded-xl object-contain max-h-[400px] bg-muted/30"
                data-testid="product-image-preview"
              />
              <button
                onClick={() => { setProductImage(null); setProductImageUrl(""); }}
                className="absolute top-2 right-2 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              {uploading && (
                <div className="absolute inset-0 bg-white/40 rounded-xl flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-[#FF5A36] animate-spin" />
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-[250px] lg:h-[350px] rounded-xl bg-muted/30 border-2 border-dashed border-border flex flex-col items-center justify-center gap-3 hover:border-muted-foreground/50 transition-colors cursor-pointer"
              data-testid="product-image-upload-btn"
            >
              <Plus className="w-8 h-8 text-muted-foreground" />
              <span className="text-muted-foreground text-sm">Select Image</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleProductUpload}
            data-testid="product-image-input"
          />
        </div>

        {/* Right: Templates */}
        <div className="bg-card rounded-2xl p-5 border border-border">
          <div className="flex items-center justify-between mb-4">
            <span className="text-foreground font-medium text-sm">Photoshoot Templates</span>
          </div>

          {/* Category filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCategory === cat
                    ? "bg-[#FF5A36] text-white"
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Template grid */}
          <div className="grid grid-cols-2 gap-3 max-h-[350px] overflow-y-auto pr-1">
            {filtered.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedTemplate(t)}
                className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                  selectedTemplate?.id === t.id
                    ? "border-[#FF5A36] ring-2 ring-[#FF5A36]/30"
                    : "border-border hover:border-muted-foreground/50"
                }`}
                data-testid={`template-card-${t.id}`}
              >
                <div className="aspect-square bg-muted">
                  {t.image_url ? (
                    <img src={t.image_url} alt={t.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-2">
                  <span className="text-white text-xs font-medium">{t.name}</span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-muted-foreground text-sm col-span-2 text-center py-8">No templates available</p>
            )}
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between">
        <AspectRatioSelector
          value={aspectRatio}
          onChange={setAspectRatio}
          show={showAspectDropdown}
          onToggle={() => setShowAspectDropdown(!showAspectDropdown)}
        />

        <Button
          onClick={handleGenerate}
          disabled={!productImageUrl || !selectedTemplate || generating}
          className="bg-[#FF5A36] hover:bg-[#e54e2e] text-white px-8 py-2.5 rounded-full font-medium disabled:opacity-40"
          data-testid="create-photoshoot-btn"
        >
          Create Photoshoot
              {creditCost !== null && (
                <span className="ml-2 inline-flex items-center gap-1 bg-white/20 text-xs px-2 py-0.5 rounded-full">
                  <Coins className="w-3 h-3" />
                  {creditCost}
                </span>
              )}
        </Button>
      </div>
    </div>
  );
}

// ============ GENERATE MODE ============
function GenerateMode({ onBack, onEdit }) {
  const [prompt, setPrompt] = useState("");
  const [referenceImages, setReferenceImages] = useState([]);
  const [referenceUrls, setReferenceUrls] = useState([]);
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [showAspectDropdown, setShowAspectDropdown] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creditCost, setCreditCost] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/photoshoot/config`, { withCredentials: true })
      .then(res => setCreditCost(res.data.credit_cost))
      .catch(() => {});
  }, []);

  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (referenceImages.length >= 3) return toast.error("Maximum 3 images");

    setUploading(true);
    const preview = URL.createObjectURL(file);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/api/photoshoot/upload-product-image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReferenceImages((prev) => [...prev, preview]);
      setReferenceUrls((prev) => [...prev, res.data.image_url]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (index) => {
    setReferenceImages((prev) => prev.filter((_, i) => i !== index));
    setReferenceUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return toast.error("Please enter a prompt");
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/photoshoot/generate/prompt`, {
        prompt,
        reference_image_urls: referenceUrls,
        aspect_ratio: aspectRatio,
      }, { withCredentials: true });
      setResult(res.data);
      toast.success("Image generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  if (result) {
    return (
      <ResultView
        imageUrl={result.generated_image_url}
        creditsUsed={result.credits_used}
        onBack={() => setResult(null)}
        onClose={onBack}
        onEdit={onEdit}
      />
    );
  }

  if (generating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in" data-testid="photoshoot-generating">
        <div className="relative w-64 h-80 bg-card rounded-2xl border border-border flex flex-col items-center justify-center gap-4 shadow-sm">
          <Loader2 className="w-8 h-8 text-[#FF5A36] animate-spin" />
          <p className="text-muted-foreground text-sm">This may take a few minutes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-in" data-testid="photoshoot-generate-mode">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="generate-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Sparkles className="w-5 h-5 text-muted-foreground" />
          <h2 className="text-xl font-semibold text-foreground">Generate or edit images</h2>
        </div>
      </div>
      <p className="text-muted-foreground text-sm mb-6 ml-8">
        Add prompt or reference images to edit - generates 1 image.
      </p>

      {/* Prompt area */}
      <div className="bg-card border border-border rounded-2xl p-5 mb-4">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Edit this image (Press + to add image)..."
          className="w-full bg-transparent text-foreground placeholder-muted-foreground text-sm resize-none outline-none min-h-[160px]"
          data-testid="prompt-textarea"
        />

        {/* Reference images strip */}
        {referenceImages.length > 0 && (
          <div className="flex gap-2 mb-3">
            {referenceImages.map((img, i) => (
              <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden">
                <img src={img} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-0.5 right-0.5 w-4 h-4 bg-black/60 rounded-full flex items-center justify-center"
                >
                  <X className="w-2.5 h-2.5 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Bottom bar */}
        <div className="flex items-center justify-between mt-2 pt-3 border-t border-border">
          <div className="flex items-center gap-3">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={referenceImages.length >= 3 || uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-full text-xs text-muted-foreground hover:bg-muted/80 transition-colors disabled:opacity-40"
              data-testid="add-images-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Add Images
            </button>

            <AspectRatioSelector
              value={aspectRatio}
              onChange={setAspectRatio}
              show={showAspectDropdown}
              onToggle={() => setShowAspectDropdown(!showAspectDropdown)}
              small
            />
          </div>

          <span className={`text-xs ${referenceImages.length >= 3 ? "text-[#FF5A36]" : "text-muted-foreground"}`}>
            ({referenceImages.length}/3 images)
          </span>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAddImage}
      />

      <p className="text-muted-foreground text-xs mb-4">AI can make mistakes, so double-check it.</p>

      <div className="flex justify-end">
        <Button
          onClick={handleGenerate}
          disabled={!prompt.trim() || generating}
          className="bg-[#FF5A36] hover:bg-[#e54e2e] text-white px-8 py-2.5 rounded-full font-medium disabled:opacity-40"
          data-testid="generate-btn"
        >
          Generate
              {creditCost !== null && (
                <span className="ml-2 inline-flex items-center gap-1 bg-white/20 text-xs px-2 py-0.5 rounded-full">
                  <Coins className="w-3 h-3" />
                  {creditCost}
                </span>
              )}
        </Button>
      </div>
    </div>
  );
}

// ============ ASPECT RATIO SELECTOR ============
function AspectRatioSelector({ value, onChange, show, onToggle, small }) {
  const current = ASPECT_RATIOS.find((a) => a.value === value);
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-1.5 bg-muted rounded-full text-muted-foreground hover:bg-muted/80 transition-colors ${
          small ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"
        }`}
        data-testid="aspect-ratio-selector"
      >
        {current?.label}
        <ChevronDown className={small ? "w-3 h-3" : "w-3.5 h-3.5"} />
      </button>
      {show && (
        <div className="absolute bottom-full mb-2 left-0 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[170px] z-50">
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar.value}
              onClick={() => { onChange(ar.value); onToggle(); }}
              className={`w-full text-left px-4 py-2 text-xs hover:bg-muted transition-colors ${
                value === ar.value ? "text-[#FF5A36]" : "text-foreground"
              }`}
            >
              {ar.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ EDIT VIEW ============
function EditView({ initialImageUrl, onBack, onClose }) {
  const navigate = useNavigate();
  const [versions, setVersions] = useState([{ url: initialImageUrl, label: "Original" }]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editPrompt, setEditPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [showAspectDropdown, setShowAspectDropdown] = useState(false);
  const [editing, setEditing] = useState(false);
  const [creditCost, setCreditCost] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/photoshoot/config`, { withCredentials: true })
      .then(res => setCreditCost(res.data.credit_cost))
      .catch(() => {});
  }, []);

  const activeImage = versions[activeIndex]?.url;

  const handleEdit = async () => {
    if (!editPrompt.trim()) return toast.error("Describe the edit you want to make");
    setEditing(true);
    try {
      const res = await axios.post(`${API}/api/photoshoot/generate/prompt`, {
        prompt: editPrompt,
        reference_image_urls: [activeImage],
        aspect_ratio: aspectRatio,
      }, { withCredentials: true });
      const newVersion = { url: res.data.generated_image_url, label: editPrompt.slice(0, 40) };
      setVersions((prev) => [...prev, newVersion]);
      setActiveIndex(versions.length);
      setEditPrompt("");
      toast.success("Edit applied!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Edit failed");
    } finally {
      setEditing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const a = document.createElement("a");
      a.href = `${API}/api/download-video?url=${encodeURIComponent(activeImage)}`;
      a.download = `photoshoot-edit-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Image downloaded!");
    } catch {
      toast.error("Download failed");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEdit();
    }
  };

  return (
    <div className="animate-fade-in" data-testid="photoshoot-edit-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="edit-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="edit-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex gap-6 flex-col lg:flex-row">
        {/* Left: Image display + thumbnails */}
        <div className="flex-1 flex flex-col items-center">
          <div className="rounded-2xl overflow-hidden bg-card border border-border w-full max-w-lg shadow-sm relative">
            {editing && (
              <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center rounded-2xl">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 text-[#FF5A36] animate-spin" />
                  <p className="text-muted-foreground text-sm">Applying edit...</p>
                </div>
              </div>
            )}
            <img
              src={activeImage}
              alt="Current version"
              className="w-full object-contain"
              data-testid="edit-main-image"
            />
          </div>

          {/* Thumbnail strip */}
          {versions.length > 1 && (
            <div className="flex gap-2 mt-4 overflow-x-auto pb-1 max-w-lg w-full justify-center" data-testid="edit-thumbnail-strip">
              {versions.map((v, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`w-14 h-14 rounded-lg overflow-hidden border-2 shrink-0 transition-all ${
                    activeIndex === i
                      ? "border-[#FF5A36] ring-2 ring-[#FF5A36]/30"
                      : "border-border hover:border-muted-foreground/50"
                  }`}
                  data-testid={`edit-thumbnail-${i}`}
                >
                  <img src={v.url} alt={v.label} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Tools panel */}
        <div className="w-full lg:w-80 shrink-0">
          <div className="bg-card border border-border rounded-2xl p-5">
            <h3 className="text-foreground font-semibold text-base mb-4">Tools</h3>

            {/* Edit prompt textarea + send */}
            <div className="bg-muted/30 border border-border rounded-xl p-3 mb-4">
              <textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe the edit you would like to make.."
                className="w-full bg-transparent text-foreground placeholder-muted-foreground text-sm resize-none outline-none min-h-[100px]"
                disabled={editing}
                data-testid="edit-prompt-textarea"
              />
              <div className="flex items-center justify-between mt-2">
                <AspectRatioSelector
                  value={aspectRatio}
                  onChange={setAspectRatio}
                  show={showAspectDropdown}
                  onToggle={() => setShowAspectDropdown(!showAspectDropdown)}
                  small
                />
                <div className="flex items-center gap-2">
                  {creditCost !== null && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-medium">
                      <Coins className="w-3 h-3" />
                      {creditCost} credits
                    </span>
                  )}
                  <button
                    onClick={handleEdit}
                    disabled={!editPrompt.trim() || editing}
                    className="w-9 h-9 rounded-full bg-[#FF5A36] hover:bg-[#e54e2e] flex items-center justify-center text-white disabled:opacity-40 transition-colors"
                    data-testid="edit-send-btn"
                  >
                    {editing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Download */}
            <Button
              onClick={handleDownload}
              className="w-full bg-[#FF5A36] hover:bg-[#e54e2e] text-white rounded-full font-medium flex items-center justify-center gap-2"
              data-testid="edit-download-btn"
            >
              <Download className="w-4 h-4" />
              Download image
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate("/photoshoot/gallery")}
              className="w-full rounded-full mt-2"
              data-testid="edit-view-all-btn"
            >
              View All Photoshoots
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ RESULT VIEW ============
function ResultView({ imageUrl, productImageUrl, templateImage, templateName, creditsUsed, onBack, onClose, onEdit }) {
  const navigate = useNavigate();
  const handleDownload = async () => {
    try {
      const a = document.createElement("a");
      a.href = `${API}/api/download-video?url=${encodeURIComponent(imageUrl)}`;
      a.download = `photoshoot-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Image downloaded!");
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div className="animate-fade-in" data-testid="photoshoot-result">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="result-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="result-close-btn">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar info */}
        {(productImageUrl || templateImage) && (
          <div className="hidden lg:block w-28 shrink-0 space-y-4">
            {productImageUrl && (
              <div>
                <span className="text-muted-foreground text-xs font-medium block mb-1.5">Product Image</span>
                <img src={productImageUrl} alt="Product" className="w-20 h-20 object-cover rounded-lg border border-border" />
              </div>
            )}
            {templateImage && (
              <div>
                <span className="text-muted-foreground text-xs font-medium block mb-1.5">Template</span>
                <img src={templateImage} alt="Template" className="w-20 h-20 object-cover rounded-lg border border-border" />
                {templateName && <span className="text-muted-foreground text-xs mt-1 block">{templateName}</span>}
              </div>
            )}
          </div>
        )}

        {/* Main image */}
        <div className="flex-1 flex flex-col items-center">
          <div className="rounded-2xl overflow-hidden bg-card border border-border max-w-lg w-full shadow-sm">
            <img
              src={imageUrl}
              alt="Generated"
              className="w-full object-contain"
              data-testid="generated-image"
            />
          </div>

          {creditsUsed > 0 && (
            <p className="text-muted-foreground text-xs mt-3">{creditsUsed} credits used</p>
          )}

          <div className="flex justify-center gap-3 mt-4 flex-wrap">
            <Button
              onClick={handleDownload}
              className="bg-[#FF5A36] hover:bg-[#e54e2e] text-white px-6 py-2.5 rounded-full font-medium flex items-center gap-2"
              data-testid="download-image-btn"
            >
              <Download className="w-4 h-4" />
              Download Image
            </Button>
            {onEdit && (
              <Button
                onClick={() => onEdit(imageUrl)}
                variant="outline"
                className="rounded-full px-6 py-2.5 flex items-center gap-2"
                data-testid="edit-image-btn"
              >
                <Pencil className="w-4 h-4" />
                Edit Image
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate("/photoshoot/gallery")}
              className="rounded-full px-6 py-2.5"
              data-testid="view-all-photoshoots-btn"
            >
              View All Photoshoots
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============ MAIN PHOTOSHOOT PAGE ============
export default function Photoshoot() {
  const [mode, setMode] = useState("landing");
  const [editImageUrl, setEditImageUrl] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.editImageUrl) {
      setEditImageUrl(location.state.editImageUrl);
      setMode("edit");
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  const handleEdit = (imageUrl) => {
    setEditImageUrl(imageUrl);
    setMode("edit");
  };

  return (
    <div
      className="min-h-[calc(100vh-2rem)] p-6 sm:p-8"
      data-testid="photoshoot-page"
    >
      {mode === "landing" && <LandingView onSelectMode={setMode} />}
      {mode === "template" && <TemplateMode onBack={() => setMode("landing")} onEdit={handleEdit} />}
      {mode === "generate" && <GenerateMode onBack={() => setMode("landing")} onEdit={handleEdit} />}
      {mode === "edit" && (
        <EditView
          initialImageUrl={editImageUrl}
          onBack={() => setMode("landing")}
          onClose={() => setMode("landing")}
        />
      )}
    </div>
  );
}
